import { get, writable } from 'svelte/store';
import { candidates, friends, mapView, trips } from './state';
import { fetchApproxLocation } from './api';
import type { ApproxLocation, LatLng, MapViewState } from './types';

export const DEFAULT_MAP_VIEW: MapViewState = { lat: 20, lng: 0, zoom: 2 };
/** Best available non-prompting location for the current viewer (IP/edge based). */
export const viewerLocation = writable<ApproxLocation | null>(null);
let viewerLocationPromise: Promise<ApproxLocation | null> | null = null;

/** Fetch once per page load and share the result with initial-view + map controls. */
export function loadViewerLocation(): Promise<ApproxLocation | null> {
  if (!viewerLocationPromise) {
    viewerLocationPromise = fetchApproxLocation().then((location) => {
      viewerLocation.set(location);
      return location;
    });
  }
  return viewerLocationPromise;
}

const TIMEZONE_CENTERS: Record<string, Omit<ApproxLocation, 'source'>> = {
  'America/Los_Angeles': { lat: 34.0522, lng: -118.2437, city: 'Los Angeles', country: 'US', timezone: 'America/Los_Angeles' },
  'America/New_York': { lat: 40.7128, lng: -74.006, city: 'New York', country: 'US', timezone: 'America/New_York' },
  'America/Mexico_City': { lat: 19.4326, lng: -99.1332, city: 'Mexico City', country: 'MX', timezone: 'America/Mexico_City' },
  'America/Sao_Paulo': { lat: -23.5505, lng: -46.6333, city: 'Sao Paulo', country: 'BR', timezone: 'America/Sao_Paulo' },
  'Europe/London': { lat: 51.5074, lng: -0.1278, city: 'London', country: 'GB', timezone: 'Europe/London' },
  'Europe/Paris': { lat: 48.8566, lng: 2.3522, city: 'Paris', country: 'FR', timezone: 'Europe/Paris' },
  'Europe/Berlin': { lat: 52.52, lng: 13.405, city: 'Berlin', country: 'DE', timezone: 'Europe/Berlin' },
  'Europe/Madrid': { lat: 40.4168, lng: -3.7038, city: 'Madrid', country: 'ES', timezone: 'Europe/Madrid' },
  'Asia/Tokyo': { lat: 35.6762, lng: 139.6503, city: 'Tokyo', country: 'JP', timezone: 'Asia/Tokyo' },
  'Asia/Seoul': { lat: 37.5665, lng: 126.978, city: 'Seoul', country: 'KR', timezone: 'Asia/Seoul' },
  'Asia/Singapore': { lat: 1.3521, lng: 103.8198, city: 'Singapore', country: 'SG', timezone: 'Asia/Singapore' },
  'Asia/Kolkata': { lat: 28.6139, lng: 77.209, city: 'Delhi', country: 'IN', timezone: 'Asia/Kolkata' },
  'Australia/Sydney': { lat: -33.8688, lng: 151.2093, city: 'Sydney', country: 'AU', timezone: 'Australia/Sydney' },
};

export function currentEntityPoints(): LatLng[] {
  return [
    ...get(friends),
    ...get(trips),
    ...get(candidates),
  ].map((p) => ({ lat: p.lat, lng: p.lng }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
}

export function viewFromPoints(points: LatLng[]): MapViewState | null {
  if (!points.length) return null;
  const lat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
  const lng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
  return { lat, lng, zoom: points.length === 1 ? 13 : 11 };
}

export function timezoneApproxLocation(): ApproxLocation | null {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const center = timezone ? TIMEZONE_CENTERS[timezone] : null;
    return center ? { ...center, source: 'timezone' } : null;
  } catch {
    return null;
  }
}

async function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((resolve) => { timer = setTimeout(() => resolve(fallback), ms); }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function resolveInitialMapView(): Promise<MapViewState> {
  const savedView = get(mapView);
  if (savedView) return savedView;

  const dataView = viewFromPoints(currentEntityPoints());
  if (dataView) return dataView;

  const edgeLocation = await withTimeout(loadViewerLocation(), 900, null);
  if (edgeLocation) return { lat: edgeLocation.lat, lng: edgeLocation.lng, zoom: 11 };

  const timezoneLocation = timezoneApproxLocation();
  if (timezoneLocation) return { lat: timezoneLocation.lat, lng: timezoneLocation.lng, zoom: 10 };

  return DEFAULT_MAP_VIEW;
}
